import { Variation } from '@/@core/services/app.service'
import { TableColumnsType } from 'antd'
import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  Stack,
  Typography,
  Box,
  Chip,
  IconButton,
  Badge,
  Slide,
  Button
} from '@mui/material'
import { ColumnDef, createColumnHelper } from '@tanstack/react-table'

import Tooltip, { tooltipClasses, TooltipProps } from '@mui/material/Tooltip'
import { useContext } from 'react'
import { AbilityContext } from '@/layouts/components/acl/Can'
import { styled } from '@mui/material/styles'
import CommentOutline from 'mdi-material-ui/CommentOutline'
import { ExternalLinks } from '@/@core/services/api'
import {
  ChevronDown,
  ChevronRight,
  Circle,
  CircleHalfFull,
  MenuDown
} from 'mdi-material-ui'
import {
  addCommas,
  chromosomeRegion,
  formatVariantInheritance,
  isJson,
  isWindow,
  renameChildTypes,
  unique
} from '@/@core/utils/utils'
import { useRouter } from 'next/router'
import { Rating } from '@mui/material'

interface Props {
  setCurrent: any
  setOpen: any
  setHistoryOpen: any
  setCommentsOpen: any
  setTranscript_list: any
  handleMenuClick: any
  getColumnSearchProps: any
  setFilterGeneOpen: any
  setMenuType: any
  setFilterGeneData: any
  getFilterGene: any
  setIndexOpen: any
  setGeneIndexOpen: any
  setGeneOpen: any
  setAcmgOpen: any
  setOMIMOpen: any
  setIgvOpen: any
  setIgvLocus: any
  setCnvScipOpen: any
  setCnvAllOpen: any
  setSampleInfoOpen: any
  setSamplesOpen: any
  VariationGeneRef: any
  analysisInfo: any
  query: any
  indexChecked: boolean
}
const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  // [`& .${tooltipClasses.tooltip}`]: {
  //   backgroundColor: 'rgba(97, 97, 97)',
  //   color: 'rgb(255, 255, 255)',
  //   maxWidth: 220,
  //   fontSize: theme.typography.pxToRem(12),
  //   border: '1px solid #dadde9'
  // }
}))

const TypographyA = styled(Typography)(() => ({
  fontSize: '0.8rem',
  color: 'inherit'
}))

const Badges = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    right: '-10px',
    backgroundColor: 'rgb(110,200,229)'
  },
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2)
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1)
  }
}))

const CustomWidthTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 500,
    maxHeight: 400,
    overflow: 'auto'
  }
})

const color = [
  {
    key: 'pathogenic',
    value: 'pathogenic',
    color: 'rgba(229,38,35,0.8)'
  },
  {
    key: 'likely pathogenic',
    value: 'likely pathogenic',
    color: 'rgba(221,109,32,0.8)'
  },
  {
    key: 'drug response',
    value: 'drug response',
    color: 'rgb(46, 125, 50,0.8)'
  },
  {
    key: 'uncertain significance',
    value: 'uncertain significance',
    color: 'rgb(240,239,240,0.4)'
  },
  {
    key: 'likely benign',
    value: 'likely benign',
    color: 'rgb(107,148,200,0.8)'
  },
  {
    key: 'benign',
    value: 'benign',
    color: 'rgb(109,56,149,0.8)'
  },
  {
    key: 'loss of function',
    value: 'loss of function',
    color: 'rgb(237, 108, 2,0.8)'
  }
]

const GLScolor = [
  {
    key: 'pathogenic',
    value: 'pathogenic',
    color: 'rgb(218,150,148)'
  },
  {
    key: 'likely pathogenic',
    value: 'likely benign',
    color: 'rgb(245,182,193)'
  },
  {
    key: 'drug response',
    value: 'drug response',
    color: 'rgb(186,204,147)'
  },
  {
    key: 'uncertain significance',
    value: 'uncertain significance',
    color: 'rgb(240,239,240)'
  },
  {
    key: 'likely benign',
    value: 'likely benign',
    color: 'rgb(107,148,200)'
  },
  {
    key: 'benign',
    value: 'benign',
    color: 'rgb(64,113,190)'
  },
  {
    key: 'loss of function',
    value: 'loss of function',
    color: 'rgb(246,209,139)'
  }
]

export const judgeType = (str: string, arr: any[]) => {
  if (str.includes('child') && !str.includes('//')) {
    return 'C'
  } else if (str.includes('child') && str.includes('//')) {
    return `C${str.split('//')[1]}`
  } else if (str.includes('mother')) {
    return 'M'
  } else if (str.includes('father')) {
    return 'F'
  } else {
    return 'O'
  }
}

const columns1CohortSites = React.forwardRef((props: Props, ref) => {
  const { t, i18n } = useTranslation()
  const ability = useContext(AbilityContext)

  const router = useRouter()

  const { id, ids, group } = router.query

  const type = router.asPath.includes('/samples') ? 'sample' : 'analyze'
  const user = JSON.parse(localStorage.getItem('user') || '{}')

  const column: ColumnDef<Variation>[] = React.useMemo(() => {
    let column: ColumnDef<Variation>[] = [
      // {
      //   accessorKey: 'grading',
      //   id: 'grading',
      //   header: () => <span>{t('Classification')}</span>,
      //   footer: (props) => props.column.id,
      //   cell: ({ row, getValue }) => {
      //     if (row.original.user_classification) {
      //       const bgcolor = GLScolor.find(
      //         (i) => i.key === row.original.user_classification
      //       )
      //         ? (color.find((i) => i.key === row.original.user_classification)
      //             ?.color as
      //             | 'rgb(240,239,240,0.4)'
      //             | 'rgba(221,109,32,0.8)'
      //             | 'rgb(255,242,202)'
      //             | 'rgb(245,182,193)')
      //         : 'rgba(0, 0, 0)'

      //       return (
      //         <Stack direction={'column'} minWidth={'60px'}>
      //           <Box sx={{ textAlign: 'center' }}>
      //             <Chip
      //               key={row.original.user_classification}
      //               size="small"
      //               label={`${row.original.user_classification
      //                 .split(' ')
      //                 .map((word) => {
      //                   if (word.includes('Uncertain')) return `VU`
      //                   if (word.includes('uncertain')) return `VU`
      //                   if (word.includes('null')) return ``
      //                   else return word[0].toUpperCase()
      //                 })
      //                 .join('')}`}
      //               sx={{
      //                 bgcolor: bgcolor,
      //                 color:
      //                   bgcolor === 'rgb(240,239,240,0.4)' ? 'black' : 'white',
      //                 justifyContent: 'center',
      //                 fontSize: '0.75rem',
      //                 fontWeight: 700,
      //                 textAlign: 'center',
      //                 minWidth: '45px'
      //               }}
      //               onClick={() => {
      //                 props.setCurrent(row.original)

      //                 props.setOpen(true)
      //               }}
      //             />
      //           </Box>
      //         </Stack>
      //       )
      //     } else {
      //       return (
      //         <Stack
      //           direction={'column'}
      //           width="100%"
      //           height={'100%'}
      //           minWidth={'60px'}
      //         >
      //           <Box textAlign={'center'}>
      //             <IconButton
      //               aria-label="edit"
      //               onClick={() => {
      //                 props.setCurrent(row.original)

      //                 props.setOpen(true)
      //               }}
      //               sx={{
      //                 padding: 0
      //               }}
      //             >
      //               <CommentOutline
      //                 sx={{
      //                   fontSize: '1rem'
      //                 }}
      //               />
      //             </IconButton>
      //           </Box>
      //         </Stack>
      //       )
      //     }
      //   }
      // },

      {
        accessorKey: 'gene',
        id: 'gene',
        header: () => (
          <Stack maxWidth={'100%'} justifyContent="start" direction={'row'}>
            {/* <Filters keys="gene" /> */}

            <Stack maxWidth={'100%'}>
              <span
                style={{
                  lineHeight: 'normal',
                  textAlign: 'left'
                }}
              >
                {t('Gene')}
              </span>

              <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                {t('TranscriptID')}
              </span>
            </Stack>
          </Stack>
        ),
        cell: ({ row, getValue }) => {
          let bgcolor = color.find(
            (i) => i.key === row.original.CLINSIG_final?.toLowerCase()
          )
            ? (color.find(
                (i) => i.key === row.original.CLINSIG_final?.toLowerCase()
              )?.color as any)
            : 'rgba(0, 0, 0)'

          const badges = row.original.user_variant_comments
            ? row.original.user_variant_comments.comments.length
            : 0

          const hisBadges = row.original.tempHistory
            ? row.original.tempHistory.length
            : 0

          // loss_of_function等于1 并且CLINSIG_final不等于P/LP
          if (row.original.CLINSIG_final?.toLowerCase() === 'pathogenic') {
            //
          } else if (
            row.original.CLINSIG_final?.toLowerCase() === 'likely pathogenic'
          ) {
            //
          } else if (row.original.loss_of_function === 1) bgcolor = '#f5d18c'
          const NM = `${row.original.transcript}(MANE)`

          return (
            <Stack
              maxWidth={'150px'}
              width={'100%'}
              marginTop={'12px'}
              direction="column"
              alignItems="start"
            >
              <Stack
                direction="row"
                alignItems={'center'}
                justifyContent="space-between"
                width={'100%'}
              >
                {ability &&
                ability.can('read', 'report-search') &&
                hisBadges ? (
                  <Badges
                    onClick={(e) => {
                      props.setHistoryOpen(true)

                      props.setCurrent(row.original)
                    }}
                    showZero
                    badgeContent={hisBadges}
                    color="primary"
                    sx={{
                      cursor: 'pointer',
                      color: 'inherit',
                      '& .MuiBadge-badge': {
                        top: '6px',
                        right: '-45px'
                      }
                    }}
                    anchorOrigin={{
                      vertical: 'top',
                      horizontal: 'right'
                    }}
                  >
                    <Badges
                      onClick={(e) => {
                        props.setCommentsOpen(true)

                        //

                        props.setCurrent(row.original)
                      }}
                      showZero
                      badgeContent={badges}
                      color="primary"
                      sx={{
                        cursor: 'pointer',
                        color: 'inherit',
                        '& .MuiBadge-badge': {
                          top: '6px',
                          right: '-18px'
                        }
                      }}
                      anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'right'
                      }}
                    >
                      <TypographyA
                        sx={{
                          color:
                            bgcolor === 'rgba(229,38,35,0.8)'
                              ? 'white'
                              : bgcolor === 'rgb(107,148,200,0.8)' ||
                                bgcolor === 'rgb(109,56,149,0.8)'
                              ? 'black'
                              : '#23527c',
                          fontWeight: '700'
                        }}
                      >
                        {row.original.gene}
                      </TypographyA>
                    </Badges>
                  </Badges>
                ) : (
                  <Badges
                    onClick={(e) => {
                      props.setCommentsOpen(true)

                      //

                      props.setCurrent(row.original)
                    }}
                    showZero
                    badgeContent={badges}
                    color="primary"
                    sx={{
                      cursor: 'pointer',
                      color: 'inherit',
                      '& .MuiBadge-badge': {
                        top: '6px',
                        right: '-18px'
                      }
                    }}
                    anchorOrigin={{
                      vertical: 'top',
                      horizontal: 'right'
                    }}
                  >
                    <TypographyA
                      sx={{
                        color:
                          bgcolor === 'rgba(229,38,35,0.8)'
                            ? 'white'
                            : bgcolor === 'rgb(107,148,200,0.8)' ||
                              bgcolor === 'rgb(109,56,149,0.8)'
                            ? 'black'
                            : '#23527c',
                        fontWeight: '700'
                      }}
                    >
                      {row.original.gene}
                    </TypographyA>
                  </Badges>
                )}

                {row && row.getCanExpand && row.getCanExpand() ? (
                  <button
                    {...{
                      onClick: row.getToggleExpandedHandler(),
                      style: { cursor: 'pointer' }
                    }}
                  >
                    {row.getIsExpanded() ? (
                      <IconButton
                        sx={{
                          '& .MuiSvgIcon-root': {
                            color: 'black'
                          },
                          padding: 0
                        }}
                      >
                        <ChevronDown />
                      </IconButton>
                    ) : (
                      <IconButton
                        sx={{
                          '& .MuiSvgIcon-root': {
                            color: 'black'
                          },
                          padding: 0
                        }}
                      >
                        <ChevronRight />
                      </IconButton>
                    )}
                  </button>
                ) : null}
              </Stack>

              <Stack
                sx={{
                  position: 'relative'
                }}
                maxWidth={'100%'}
                direction="row"
                alignItems="center"
              >
                <Tooltip
                  title={`${row.original.transcript}(MANE)`}
                  placement={'top-start'}
                  disableHoverListener={NM.length <= 14}
                >
                  <TypographyA
                    id="qw"
                    sx={{
                      width: '100%',
                      color:
                        bgcolor === 'rgba(229,38,35,0.8)'
                          ? 'white'
                          : bgcolor === 'rgb(107,148,200,0.8)' ||
                            bgcolor === 'rgb(109,56,149,0.8)'
                          ? 'black'
                          : '#23527c',
                      maxWidth: 140,
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      cursor: 'pointer'
                    }}
                    onClick={() => {
                      window.open(
                        `${ExternalLinks.ncbi.nuccore}/${row.original.transcript}`
                      )
                    }}
                  >
                    {NM.length >= 14 ? `${NM}` : NM}
                  </TypographyA>
                </Tooltip>

                {/* <IconButton
                  onClick={(e) => {
                    props.setTranscript_list(row.original.transcript_list)

                    props.handleMenuClick(e, 'list')
                  }}
                  sx={{
                    color: 'inherit',
                    padding: 0

                    // position: 'absolute',
                    // right: 0,
                    // bottom: '-1rem'
                  }}
                  size="small"
                >
                  <MenuDown />
                </IconButton> */}
              </Stack>
            </Stack>
          )
        },
        footer: (props) => props.column.id
      },
      {
        accessorKey: 'dbSNP',
        id: 'dbSNP',
        header: () => (
          <Stack maxWidth="100%">
            <Stack maxWidth="100%">
              <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                {t('Position')}
              </span>

              <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                {t('dbSNP')}
              </span>
            </Stack>
          </Stack>
        ),
        footer: (props) => props.column.id,
        cell: ({ row, getValue }) => {
          let bgcolor = color.find(
            (i) => i.key === row.original.CLINSIG_final?.toLowerCase()
          )
            ? (color.find(
                (i) => i.key === row.original.CLINSIG_final?.toLowerCase()
              )?.color as any)
            : 'rgba(0, 0, 0)'

          // loss_of_function等于1 并且CLINSIG_final不等于P/LP
          if (row.original.CLINSIG_final?.toLowerCase() === 'pathogenic') {
            //
          } else if (
            row.original.CLINSIG_final?.toLowerCase() === 'likely pathogenic'
          ) {
            //
          } else if (row.original.loss_of_function === 1) bgcolor = '#f5d18c'

          return (
            <Stack maxWidth={'100%'} direction="column" spacing={1}>
              <TypographyA
                sx={{
                  cursor: 'pointer',
                  color:
                    bgcolor === 'rgba(229,38,35,0.8)'
                      ? 'white'
                      : bgcolor === 'rgb(107,148,200,0.8)' ||
                        bgcolor === 'rgb(109,56,149,0.8)'
                      ? 'black'
                      : '#23527c',
                  fontWeight: '700'
                }}
                onClick={(e) => {
                  if (
                    (props.analysisInfo && props.analysisInfo.bamFile) ||
                    props.analysisInfo.sampleType === 'fastq'
                  ) {
                    props.handleMenuClick(e, 'IGV')

                    props.setCurrent(row.original)
                  } else if (
                    props.analysisInfo.analysisType === 'family_analysis' ||
                    props.analysisInfo.analysisType === 'big_family'
                  ) {
                    const fqSamples = props.analysisInfo.samples?.filter(
                      (item: any) => item && item.sampleType === 'fastq'
                    )

                    //

                    if (fqSamples && fqSamples.length) {
                      props.handleMenuClick(e, 'IGV')

                      props.setCurrent(row.original)
                    } else {
                      window.open(
                        `${ExternalLinks.genome}?db=${
                          props.analysisInfo.genomeBuild ?? 'hg19'
                        }&lastVirtModeType=default&lastVirtModeExtraState=&virtModeType=default&virtMode=0&nonVirtPosition=&position=chr${
                          row.original.chrom
                        }:${row.original.pos}-${row.original.pos}`
                      )
                    }
                  } else
                    window.open(
                      `${ExternalLinks.genome}?db=${
                        props.analysisInfo.genomeBuild ?? 'hg19'
                      }&lastVirtModeType=default&lastVirtModeExtraState=&virtModeType=default&virtMode=0&nonVirtPosition=&position=chr${
                        row.original.chrom
                      }:${row.original.pos}-${row.original.pos}`
                    )
                }}
              >
                {`${row.original.chrom}:${row.original.pos}`}
              </TypographyA>

              <Tooltip
                disableHoverListener={row.original.rs_id?.length <= 14}
                title={row.original.rs_id}
                placement={'top-start'}
              >
                <TypographyA
                  sx={{
                    color:
                      bgcolor === 'rgba(229,38,35,0.8)'
                        ? 'white'
                        : bgcolor === 'rgb(107,148,200,0.8)' ||
                          bgcolor === 'rgb(109,56,149,0.8)'
                        ? 'black'
                        : '#23527c',
                    cursor: 'pointer',
                    width: '100%',
                    maxWidth: 140,
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    fontWeight: '700'
                  }}
                  onClick={() => {
                    window.open(
                      `${ExternalLinks.ncbi.snp_ref}?type=rs&rs=${row.original.rs_id}`
                    )
                  }}
                >
                  {`${row.original.rs_id}`}
                </TypographyA>
              </Tooltip>
            </Stack>
          )
        }
      },
      {
        accessorKey: 'coding_effect',
        id: 'coding_effect',
        enableSorting: false,
        header: () => (
          <Stack maxWidth={'100%'} direction={'row'} alignItems="center">
            <Stack maxWidth={'100%'}>
              <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                {t('Function')}
              </span>

              <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                {`${t('Location')}`}
              </span>
            </Stack>
          </Stack>
        ),
        footer: (props) => props.column.id,
        cell: ({ row, getValue }) => (
          <Stack maxWidth={'100%'} direction="column" spacing={1}>
            <Tooltip
              disableHoverListener={row.original.coding_effect?.length <= 14}
              placement={'top-start'}
              title={row.original.coding_effect}
            >
              <TypographyA
                sx={{
                  maxWidth: 100,
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis'
                }}
              >{`${row.original.coding_effect}`}</TypographyA>
            </Tooltip>

            <Tooltip
              disableHoverListener={
                row.original.var_location?.length + row.original.exon?.length <=
                14
              }
              placement={'top-start'}
              title={`${row.original.var_location}${
                row.original.exon === '.' ? '' : `(${row.original.exon})`
              }`}
            >
              <TypographyA
                sx={{
                  maxWidth: 100,
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis'
                }}
              >{`${row.original.var_location}${
                row.original.exon === '.' ? '' : `(${row.original.exon})`
              }`}</TypographyA>
            </Tooltip>
          </Stack>
        )
      },
      {
        accessorKey: 'nuc_change',
        id: 'nuc_change',
        enableSorting: false,
        header: () => (
          <React.Fragment>
            <Stack maxWidth="100%">
              <span
                style={{
                  textAlign: 'start'
                }}
              >
                {t('Base change')}
              </span>
            </Stack>
          </React.Fragment>
        ),
        footer: (props) => props.column.id,
        cell: ({ row, getValue }) => (
          <Stack maxWidth={'100%'} direction="column" spacing={1}>
            <Box>
              <Tooltip
                disableHoverListener={row.original.nuc_change?.length <= 14}
                title={`${row.original.nuc_change}`}
                placement="top-start"
              >
                <TypographyA
                  sx={{
                    maxWidth: 140,
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis'
                  }}
                >
                  {`${row.original.nuc_change}`}
                </TypographyA>
              </Tooltip>

              <Tooltip
                disableHoverListener={row.original.c_nomen?.length <= 14}
                title={`${row.original.c_nomen}`}
                placement="top-start"
              >
                <TypographyA
                  sx={{
                    maxWidth: 140,
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis'
                  }}
                >
                  {`${row.original.c_nomen}`}
                </TypographyA>
              </Tooltip>

              <Tooltip
                disableHoverListener={row.original.p_nomen?.length <= 14}
                title={`${row.original.p_nomen}`}
                placement="top-start"
              >
                <TypographyA
                  sx={{
                    maxWidth: 140,
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis'
                  }}
                >
                  {`${row.original.p_nomen}`}
                </TypographyA>
              </Tooltip>
            </Box>
          </Stack>
        )
      },
      {
        accessorKey: 'index',
        id: 'index',
        enableSorting: false,
        header: () => (
          <Tooltip
            title={
              user && user.organization && user.organization.isShared ? (
                <>
                  <p>组织下有该基因的样本量/平台有该基因的样本量</p>

                  <p>组织下有该变异的样本量/组织下的总样本量</p>

                  <p>平台上有该变异的样本量/平台上的总样本量</p>
                </>
              ) : (
                <>
                  <p>账户下有该基因的样本量/平台有该基因的样本量</p>

                  <p>账户下有该变异的样本量/账户下的总样本量</p>

                  <p>平台上有该变异的样本量/平台上的总样本量</p>
                </>
              )
            }
          >
            <Stack maxWidth={'100%'} direction={'column'} alignItems="center">
              <Stack maxWidth={'100%'}>
                <React.Fragment>
                  <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                    {t('Gene Index')}
                  </span>
                </React.Fragment>
              </Stack>

              <Stack maxWidth={'100%'}>
                <React.Fragment>
                  <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                    {t('Variant Index (local)')}
                  </span>
                </React.Fragment>
              </Stack>

              <Stack maxWidth={'100%'}>
                <React.Fragment>
                  <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                    {t('Variant Index (GLS)')}
                  </span>
                </React.Fragment>
              </Stack>
            </Stack>
          </Tooltip>
        ),
        footer: (props) => props.column.id,
        cell: ({ row, getValue }) => {
          const Index = row.original.Index
          const GeneIndex = row.original.GeneIndex

          let bgcolor = color.find(
            (i) => i.key === row.original.CLINSIG_final?.toLowerCase()
          )
            ? (color.find(
                (i) => i.key === row.original.CLINSIG_final?.toLowerCase()
              )?.color as any)
            : 'rgba(0, 0, 0)'

          // loss_of_function等于1 并且CLINSIG_final不等于P/LP
          if (row.original.CLINSIG_final?.toLowerCase() === 'pathogenic') {
            //
          } else if (
            row.original.CLINSIG_final?.toLowerCase() === 'likely pathogenic'
          ) {
            //
          } else if (row.original.loss_of_function === 1) bgcolor = '#f5d18c'

          return (
            <Stack maxWidth={'100%'} direction="column" spacing={1}>
              {GeneIndex ? (
                <React.Fragment>
                  <TypographyA
                    sx={{
                      width: '100%',
                      maxWidth: 140,
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      cursor: 'pointer',
                      color:
                        bgcolor === 'rgba(229,38,35,0.8)' ? 'white' : '#23527c'
                    }}
                    onClick={() => {
                      props.setCurrent(row.original)

                      props.setGeneIndexOpen(true)
                    }}
                  >
                    {`${GeneIndex.accountTotal} / ${GeneIndex.platformTotal}`}
                  </TypographyA>
                </React.Fragment>
              ) : (
                ''
              )}

              {Index ? (
                <React.Fragment>
                  <TypographyA
                    sx={{
                      width: '100%',
                      maxWidth: 140,
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      cursor: 'pointer',
                      color:
                        bgcolor === 'rgba(229,38,35,0.8)' ? 'white' : '#23527c'
                    }}
                    onClick={() => {
                      props.setCurrent(row.original)

                      props.setIndexOpen(true)
                    }}
                  >
                    {user && user.organization && user.organization.isShared
                      ? `${row.original.Index.organizationTotal} / ${row.original.Index.organizationTotalSamples}`
                      : `${row.original.Index.accountTotal} / ${row.original.Index.userTotalSamples}`}
                  </TypographyA>

                  <TypographyA
                    sx={{
                      width: '100%',
                      maxWidth: 140,
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      cursor: 'pointer',
                      color:
                        bgcolor === 'rgba(229,38,35,0.8)' ? 'white' : '#23527c'
                    }}
                    onClick={() => {
                      props.setCurrent(row.original)

                      props.setIndexOpen(true)
                    }}
                  >
                    {`${row.original.Index.platformTotal} / ${
                      row.original.Index.glsTotalSamples +
                      row.original.Index.btgTotalSamples
                    }`}
                  </TypographyA>
                </React.Fragment>
              ) : (
                ''
              )}
            </Stack>
          )
        }
      },
      {
        accessorKey: 'articles',
        id: 'articles',
        enableSorting: false,
        header: () => (
          <Stack maxWidth={'100%'} direction={'row'} alignItems="center">
            <Stack maxWidth={'100%'}>
              <React.Fragment>
                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('Articles')}
                </span>
              </React.Fragment>
            </Stack>
          </Stack>
        ),
        footer: (props) => props.column.id,
        cell: ({ row, getValue }) => {
          const LitVarArticles = row.original.LitVarArticles
          const Articles = row.original.Articles
          let bgcolor = color.find(
            (i) => i.key === row.original.CLINSIG_final?.toLowerCase()
          )
            ? (color.find(
                (i) => i.key === row.original.CLINSIG_final?.toLowerCase()
              )?.color as any)
            : 'rgba(0, 0, 0)'

          // loss_of_function等于1 并且CLINSIG_final不等于P/LP
          if (row.original.CLINSIG_final?.toLowerCase() === 'pathogenic') {
            //
          } else if (
            row.original.CLINSIG_final?.toLowerCase() === 'likely pathogenic'
          ) {
            //
          } else if (row.original.loss_of_function === 1) bgcolor = '#f5d18c'

          return (
            <Stack
              onClick={(e) => {
                //
                e.stopPropagation()

                props.setAcmgOpen(true)

                props.setCurrent(row.original)

                setTimeout(() => {
                  props.VariationGeneRef.current &&
                    props.VariationGeneRef.current.handleClickLeft &&
                    props.VariationGeneRef.current.handleClickLeft(
                      3,
                      'Publication'
                    )
                }, 2000)
              }}
              maxWidth={'100%'}
              direction="column"
              spacing={1}
              sx={{
                cursor: 'pointer'
              }}
            >
              {LitVarArticles?.count ? (
                <TypographyA
                  sx={{
                    width: '100%',
                    maxWidth: 140,
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    color:
                      bgcolor === 'rgba(229,38,35,0.8)' ? 'white' : '#23527c'
                  }}
                >
                  LitVar:{LitVarArticles?.count ?? 0}
                </TypographyA>
              ) : (
                ''
              )}

              {Articles?.variantPubmedArticlesCount ? (
                <TypographyA
                  sx={{
                    width: '100%',
                    maxWidth: 140,
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    color:
                      bgcolor === 'rgba(229,38,35,0.8)' ? 'white' : '#23527c'
                  }}
                >
                  Clinvar:{Articles?.variantPubmedArticlesCount ?? 0}
                </TypographyA>
              ) : (
                ''
              )}

              {Articles?.genePubmedArticlesCount ? (
                <TypographyA
                  sx={{
                    width: '100%',
                    maxWidth: 140,
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    color:
                      bgcolor === 'rgba(229,38,35,0.8)' ? 'white' : '#23527c'
                  }}
                >
                  OMIM:{Articles?.genePubmedArticlesCount ?? 0}
                </TypographyA>
              ) : (
                ''
              )}
            </Stack>
          )
        }
      },
      {
        accessorKey: 'odds_ratio',
        id: 'odds_ratio',
        header: () => (
          <Stack maxWidth={'100%'} justifyContent="start" direction={'row'}>
            <Stack maxWidth={'100%'}>
              <span
                style={{
                  lineHeight: 'normal',
                  textAlign: 'left'
                }}
              >
                {t('OR')}
              </span>
            </Stack>
          </Stack>
        ),
        cell: ({ row, getValue }) => {
          return (
            <Stack
              maxWidth={'150px'}
              width={'100%'}
              marginTop={'12px'}
              direction="column"
              alignItems="start"
            >
              <TypographyA
                sx={{
                  color: '#23527c',
                  fontWeight: '700'
                }}
              >
                {row.original.odds_ratio
                  ? row.original.odds_ratio.toFixed(4)
                  : row.original.odds_ratio}
              </TypographyA>
            </Stack>
          )
        },
        footer: (props) => props.column.id
      },
      {
        accessorKey: 'p_value',
        id: 'p_value',
        header: () => (
          <Stack maxWidth={'100%'} justifyContent="start" direction={'row'}>
            <Stack maxWidth={'100%'}>
              <span
                style={{
                  lineHeight: 'normal',
                  textAlign: 'left'
                }}
              >
                {t('P-Value')}
              </span>
            </Stack>
          </Stack>
        ),
        cell: ({ row, getValue }) => {
          return (
            <Stack
              maxWidth={'150px'}
              width={'100%'}
              marginTop={'12px'}
              direction="column"
              alignItems="start"
            >
              <TypographyA
                sx={{
                  color: '#23527c',
                  fontWeight: '700'
                }}
              >
                {row.original.p_value
                  ? Number(row.original.p_value).toFixed(4)
                  : '.'}
              </TypographyA>
            </Stack>
          )
        },
        footer: (props) => props.column.id
      },
      {
        accessorKey: 'confidence_interval',
        id: 'confidence_interval',
        header: () => (
          <Stack maxWidth={'100%'} justifyContent="start" direction={'row'}>
            <Stack maxWidth={'100%'}>
              <span
                style={{
                  lineHeight: 'normal',
                  textAlign: 'left'
                }}
              >
                {t('95% confidence interval')}
              </span>
            </Stack>
          </Stack>
        ),
        cell: ({ row, getValue }) => {
          const confidence_interval = row.original.confidence_interval
          if (confidence_interval && confidence_interval.includes(':')) {
            const confidence = Number(
              confidence_interval.split(':')[0]
            ).toFixed(4)
            const interval = Number(confidence_interval.split(':')[1]).toFixed(
              4
            )

            return (
              <Stack
                maxWidth={'150px'}
                width={'100%'}
                marginTop={'12px'}
                direction="column"
                alignItems="start"
              >
                <TypographyA
                  sx={{
                    color: '#23527c',
                    fontWeight: '700'
                  }}
                >
                  {`${confidence}:${interval}`}
                </TypographyA>
              </Stack>
            )
          } else
            return (
              <Stack
                maxWidth={'150px'}
                width={'100%'}
                marginTop={'12px'}
                direction="column"
                alignItems="start"
              >
                <TypographyA
                  sx={{
                    color: '#23527c',
                    fontWeight: '700'
                  }}
                >
                  {row.original.confidence_interval}
                </TypographyA>
              </Stack>
            )
        },
        footer: (props) => props.column.id
      },
      {
        accessorKey: 'total_cases',
        id: 'total_cases',
        header: () => (
          <Stack maxWidth={'100%'} justifyContent="start" direction={'row'}>
            <Stack maxWidth={'100%'}>
              <span
                style={{
                  lineHeight: 'normal',
                  textAlign: 'left'
                }}
              >
                {t('Total Cases')}
              </span>
            </Stack>
          </Stack>
        ),
        cell: ({ row, getValue }) => {
          return (
            <Stack
              maxWidth={'150px'}
              width={'100%'}
              marginTop={'12px'}
              direction="column"
              alignItems="start"
            >
              <TypographyA
                sx={{
                  color: '#23527c',
                  fontWeight: '700',
                  cursor: 'pointer'
                }}
                onClick={() => {
                  props.setSamplesOpen(true)

                  props.setCurrent(row.original)
                }}
              >
                {row.original.case_samples
                  ? row.original.case_samples.length
                  : 0}
              </TypographyA>
            </Stack>
          )
        },
        footer: (props) => props.column.id
      },
      {
        accessorKey: 'total_controls',
        id: 'total_controls',
        header: () => (
          <Stack maxWidth={'100%'} justifyContent="start" direction={'row'}>
            <Stack maxWidth={'100%'}>
              <span
                style={{
                  lineHeight: 'normal',
                  textAlign: 'left'
                }}
              >
                {t('Total Controls')}
              </span>
            </Stack>
          </Stack>
        ),
        cell: ({ row, getValue }) => {
          return (
            <Stack
              maxWidth={'150px'}
              width={'100%'}
              marginTop={'12px'}
              direction="column"
              alignItems="start"
            >
              <TypographyA
                sx={{
                  color: '#23527c',
                  fontWeight: '700',
                  cursor: 'pointer'
                }}
                onClick={() => {
                  props.setSamplesOpen(true)

                  props.setCurrent(row.original)
                }}
              >
                {row.original.control_samples
                  ? row.original.control_samples.length
                  : 0}
              </TypographyA>
            </Stack>
          )
        },
        footer: (props) => props.column.id
      },
      {
        accessorKey: 'gnomAD_exome_ALL',
        id: 'gnomAD_exome_ALL',
        header: () => (
          <Stack maxWidth={'100%'} direction={'row'} alignItems="center">
            <Stack maxWidth={'100%'}>
              <span style={{ lineHeight: 'normal', textAlign: 'left' }}>{`${t(
                'gAD'
              )}_ALL`}</span>

              <span style={{ lineHeight: 'normal', textAlign: 'left' }}>{`${t(
                'gAD'
              )}_EAS`}</span>
            </Stack>
          </Stack>
        ),
        enableSorting: false,
        footer: (props) => props.column.id,
        cell: ({ row, getValue }) => (
          <Stack maxWidth={'100%'} direction="column">
            <TypographyA
              sx={{
                maxWidth: '100%'
              }}
            >
              {getValue<number | string>() === '.'
                ? '.'
                : getValue<number>() === 0
                ? 0
                : `${getValue<number>()?.toFixed(8)}`}
            </TypographyA>

            <TypographyA
              sx={{
                maxWidth: '100%'
              }}
            >
              {(row.original.gnomAD_exome_EAS as unknown as string) === '.'
                ? '.'
                : row.original.gnomAD_exome_EAS === 0
                ? 0
                : `${row.original.gnomAD_exome_EAS?.toFixed(8)}`}
            </TypographyA>
          </Stack>
        )
      },
      {
        accessorKey: 'SpliceAI_max_score',
        id: 'spliceAI_max_score',
        enableSorting: false,
        header: () => (
          <Stack maxWidth={'100%'} alignItems="center">
            <Tooltip
              title={`D(${t('harmful')}) U(${t('unknown significance')}) B(${t(
                'benign'
              )})`}
              placement="top-start"
            >
              <Stack maxWidth={'100%'}>
                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('SpliceAI')}
                </span>

                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('Revel')}
                </span>

                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('AlphaM')}
                </span>
              </Stack>
            </Tooltip>
          </Stack>
        ),
        footer: (props) => props.column.id,
        cell: ({ row, getValue }) => {
          return (
            <Stack maxWidth={'100%'}>
              <Stack maxWidth={'100%'} direction="column" spacing={1}>
                <TypographyA
                  sx={{
                    fontSize: '0.8rem',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    pr: '10px'
                  }}
                >
                  <span>
                    {(row.original.SpliceAI_max_score as unknown as string) ===
                    '.'
                      ? '.'
                      : `${row.original.SpliceAI_max_score?.toFixed(2)}`}
                  </span>

                  <span
                    style={{
                      fontWeight:
                        row.original.SpliceAI_max_score >= 0.2
                          ? 'bold'
                          : 'normal'
                    }}
                  >
                    {`${
                      row.original.SpliceAI_max_score >= 0.2
                        ? 'D'
                        : row.original.SpliceAI_max_score <= 0.1
                        ? ''
                        : ''
                    }`}
                  </span>
                </TypographyA>

                <TypographyA
                  sx={{
                    fontSize: '0.8rem',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    pr: '10px'
                  }}
                >
                  <span>
                    {(row.original.REVEL_score as unknown as string) === '.'
                      ? '.'
                      : `${row.original.REVEL_score?.toFixed(3)}`}
                  </span>

                  <span
                    style={{
                      fontWeight:
                        row.original.REVEL_score >= 0.644 ? 'bold' : 'normal'
                    }}
                  >
                    {`${
                      row.original.REVEL_score >= 0.644
                        ? 'D'
                        : row.original.REVEL_score <= 0.29
                        ? ''
                        : ''
                    }`}
                  </span>
                </TypographyA>

                <TypographyA
                  sx={{
                    fontSize: '0.8rem',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    pr: '10px'
                  }}
                >
                  <span>
                    {(row.original.AM_pathogenicity as unknown as string) ===
                      '.' || !row.original.AM_pathogenicity
                      ? '.'
                      : `${Number(row.original.AM_pathogenicity)?.toFixed(2)}`}
                  </span>

                  <span
                    style={{
                      fontWeight:
                        Number(row.original.AM_pathogenicity) >= 0.564
                          ? 'bold'
                          : 'normal'
                    }}
                  >
                    {`${
                      Number(row.original.AM_pathogenicity) >= 0.564
                        ? 'D'
                        : Number(row.original.AM_pathogenicity) <= 0.34
                        ? ''
                        : ''
                    }`}
                  </span>
                </TypographyA>
              </Stack>
            </Stack>
          )
        }
      },
      {
        accessorKey: 'CLINVAR_variant_id',
        id: 'cLINVAR_variant_id',
        header: () => (
          <Stack maxWidth={'100%'} direction={'row'} alignItems="center">
            <Stack maxWidth={'100%'}>
              <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                {t('Clinvar (star)')}
              </span>

              <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                {t('Curation')}
              </span>
            </Stack>
          </Stack>
        ),
        enableSorting: false,
        footer: (props) => props.column.id,
        cell: ({ row, getValue }) => {
          let bgcolor = color.find(
            (i) => i.key === row.original.CLINSIG_final?.toLowerCase()
          )
            ? (color.find(
                (i) => i.key === row.original.CLINSIG_final?.toLowerCase()
              )?.color as any)
            : 'rgba(0, 0, 0)'

          // loss_of_function等于1 并且CLINSIG_final不等于P/LP
          if (row.original.CLINSIG_final?.toLowerCase() === 'pathogenic') {
            //
          } else if (
            row.original.CLINSIG_final?.toLowerCase() === 'likely pathogenic'
          ) {
            //
          } else if (row.original.loss_of_function === 1) bgcolor = '#f5d18c'

          return (
            <Stack maxWidth={'100%'} direction={'column'} spacing={1}>
              <Stack
                alignItems={'center'}
                maxWidth={'100%'}
                spacing={1}
                direction="row"
              >
                <Tooltip
                  title={row.original.CLINVAR_clnsig_final}
                  placement={'top-start'}
                >
                  <TypographyA
                    sx={{
                      color:
                        bgcolor === 'rgba(229,38,35,0.8)' ? 'white' : '#23527c',
                      cursor: 'pointer',
                      maxWidth: 80,
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis'
                    }}
                    onClick={() => {
                      window.open(
                        `${ExternalLinks.ncbi.variation}/${getValue<string>()}`
                      )
                    }}
                  >
                    {`${row.original.CLINVAR_clnsig_final}`}
                  </TypographyA>
                </Tooltip>

                {row.original.CLINVAR_gold_stars &&
                row.original.CLINVAR_gold_stars !== '.' ? (
                  <Rating
                    readOnly
                    value={row.original.CLINVAR_gold_stars as unknown as number}
                    max={row.original.CLINVAR_gold_stars as unknown as number}
                  />
                ) : (
                  ''
                )}
              </Stack>

              <TypographyA
                sx={{
                  maxWidth: '100%'
                }}
              >
                {`${row.original.curation}`}
              </TypographyA>
            </Stack>
          )
        }
      },
      {
        accessorKey: 'ACMG_priority',
        id: 'ACMG_priority',
        header: () => (
          <React.Fragment>
            {/* <Filters keys="ACMG_classification" /> */}

            <Stack maxWidth={'100%'} direction={'column'} alignItems="center">
              <span>{t('ACMG')}</span>

              <span>{t('Classification')}</span>
            </Stack>
          </React.Fragment>
        ),
        footer: (props) => props.column.id,
        cell: ({ row, getValue }) => {
          const acmg = row.original.ACMG_classification
            ? `${row.original.ACMG_classification}`
            : row.original.user_acmg_classification
            ? `${row.original.user_acmg_classification}`
            : ''

          let bgcolor = color.find((i) => i.key === acmg?.toLowerCase())
            ? (color.find((i) => i.key === acmg?.toLowerCase())?.color as
                | 'rgb(240,239,240,0.4)'
                | 'rgba(221,109,32,0.8)'
                | 'rgb(255,242,202)'
                | 'rgb(245,182,193)')
            : 'none'
          if (row.original.user_acmg_classification) {
            bgcolor = color.find(
              (i) =>
                i.key === row.original.user_acmg_classification?.toLowerCase()
            )
              ? (color.find(
                  (i) =>
                    i.key ===
                    row.original.user_acmg_classification?.toLowerCase()
                )?.color as
                  | 'rgb(240,239,240,0.4)'
                  | 'rgba(221,109,32,0.8)'
                  | 'rgb(255,242,202)'
                  | 'rgb(245,182,193)')
              : 'rgba(0, 0, 0)'

            return (
              <Stack maxWidth={'100%'}>
                <Box
                  sx={{
                    maxWidth: '100%',
                    textAlign: 'center',
                    display: 'flex',
                    justifyContent: 'center',
                    flexDirection: 'column'
                  }}
                >
                  <Box>
                    <Chip
                      sx={{
                        bgcolor: bgcolor,
                        color:
                          bgcolor === 'rgb(240,239,240,0.4)'
                            ? 'black'
                            : 'white',
                        justifyContent: 'center',
                        fontSize: '0.75rem',
                        minWidth: '45px',
                        textAlign: 'center'
                      }}
                      label={`${row.original.user_acmg_classification
                        .split(' ')
                        .map((word) => {
                          if (word.includes('Uncertain')) return `VU`
                          if (word.includes('null')) return ``
                          else return word[0].toUpperCase()
                        })
                        .join('')}${
                        row.original.ACMG_score || row.original.ACMG_score === 0
                          ? `:${row.original.ACMG_score}`
                          : ``
                      }`}
                      size="small"
                      onClick={() => {
                        props.setAcmgOpen(true)

                        props.setCurrent(row.original)
                      }}
                    />
                  </Box>

                  <Box>
                    {row.original.user_classification ? (
                      <Chip
                        key={row.original.user_classification}
                        size="small"
                        label={`${row.original.user_classification
                          .split(' ')
                          .map((word) => {
                            if (word.includes('Uncertain')) return `VU`
                            if (word.includes('uncertain')) return `VU`
                            if (word.includes('null')) return ``
                            else return word[0].toUpperCase()
                          })
                          .join('')}`}
                        sx={{
                          bgcolor: bgcolor,
                          color:
                            bgcolor === 'rgb(240,239,240,0.4)'
                              ? 'black'
                              : 'white',
                          justifyContent: 'center',
                          fontSize: '0.75rem',
                          fontWeight: 700,
                          textAlign: 'center',
                          minWidth: '45px'
                        }}
                        onClick={() => {
                          props.setCurrent(row.original)

                          props.setOpen(true)
                        }}
                      />
                    ) : (
                      <IconButton
                        aria-label="edit"
                        onClick={() => {
                          props.setCurrent(row.original)

                          props.setOpen(true)
                        }}
                        sx={{
                          padding: 0
                        }}
                      >
                        <CommentOutline
                          sx={{
                            fontSize: '1rem'
                          }}
                        />
                      </IconButton>
                    )}
                  </Box>
                </Box>
              </Stack>
            )
          } else if (
            acmg !== '.' ||
            row.original.user_acmg_classification !== '.'
          ) {
            return (
              <Stack maxWidth={'100%'}>
                <Box
                  sx={{
                    maxWidth: '100%',
                    textAlign: 'center',
                    display: 'flex',
                    justifyContent: 'center',
                    flexDirection: 'column'
                  }}
                >
                  <Box>
                    <Chip
                      sx={{
                        bgcolor: bgcolor,
                        color:
                          bgcolor === 'rgb(240,239,240,0.4)'
                            ? 'black'
                            : 'white',
                        justifyContent: 'center',
                        fontSize: '0.75rem',
                        minWidth: '45px',
                        fontWeight: '700',
                        textAlign: 'center'
                      }}
                      label={`${acmg
                        ?.split(' ')
                        .map((word) => {
                          if (word.includes('Uncertain')) return `VU`
                          if (word.includes('null')) return ``
                          else return word[0].toUpperCase()
                        })
                        .join('')}${
                        row.original.ACMG_score || row.original.ACMG_score === 0
                          ? `:${row.original.ACMG_score}`
                          : ``
                      }`}
                      size="small"
                      onClick={() => {
                        props.setAcmgOpen(true)

                        props.setCurrent(row.original)
                      }}
                    />
                  </Box>

                  <Box>
                    {row.original.user_classification ? (
                      <Chip
                        key={row.original.user_classification}
                        size="small"
                        label={`${row.original.user_classification
                          .split(' ')
                          .map((word) => {
                            if (word.includes('Uncertain')) return `VU`
                            if (word.includes('uncertain')) return `VU`
                            if (word.includes('null')) return ``
                            else return word[0].toUpperCase()
                          })
                          .join('')}`}
                        sx={{
                          bgcolor: bgcolor,
                          color:
                            bgcolor === 'rgb(240,239,240,0.4)'
                              ? 'black'
                              : 'white',
                          justifyContent: 'center',
                          fontSize: '0.75rem',
                          fontWeight: 700,
                          textAlign: 'center',
                          minWidth: '45px'
                        }}
                        onClick={() => {
                          props.setCurrent(row.original)

                          props.setOpen(true)
                        }}
                      />
                    ) : (
                      <IconButton
                        aria-label="edit"
                        onClick={() => {
                          props.setCurrent(row.original)

                          props.setOpen(true)
                        }}
                        sx={{
                          padding: 0
                        }}
                      >
                        <CommentOutline
                          sx={{
                            fontSize: '1rem'
                          }}
                        />
                      </IconButton>
                    )}
                  </Box>
                </Box>
              </Stack>
            )
          } else
            return (
              <>
                <TypographyA
                  sx={{
                    maxWidth: '100%',
                    textAlign: 'center'
                  }}
                >
                  {`${row.original.ACMG_score}`}
                </TypographyA>

                <TypographyA textAlign={'center'}>.</TypographyA>
              </>
            )
        }
      },
      {
        accessorKey: 'inheritance',
        id: 'inheritance',
        header: () => (
          <Stack width={'100%'} direction={'row'} justifyContent="center">
            <Stack maxWidth={'100%'} justifyContent="center">
              {props.query && props.query.key && props.query.key === '5' ? (
                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('Disease Information')}
                </span>
              ) : (
                <React.Fragment>
                  <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                    {t('OMIM')}
                  </span>

                  <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                    {t('Inheritance')}
                  </span>
                </React.Fragment>
              )}
            </Stack>
          </Stack>
        ),
        enableSorting: false,
        footer: (props) => props.column.id,
        cell: ({ row, getValue }) => {
          if (props.query && props.query.key && props.query.key === '5') {
            return (
              <Stack maxWidth={'100%'} spacing={1}>
                {row.original.MitoMap && row.original.MitoMap.disease_ch ? (
                  <Tooltip
                    title={
                      <>
                        <Typography color={'white'}>
                          {row.original.MitoMap.disease_ch}
                        </Typography>

                        <Typography color={'white'}>
                          {row.original.MitoMap.info}
                        </Typography>
                      </>
                    }
                    placement={'top-start'}
                    TransitionComponent={Slide}
                    sx={{
                      '& .MuiTooltip-tooltip': {
                        maxWidth: isWindow() ? '400px' : '200px'
                      }
                    }}
                  >
                    <Box>
                      <TypographyA
                        sx={{
                          width: '100%',
                          maxWidth: 200,
                          lineHeight: '24px',
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                          textOverflow: 'ellipsis',
                          cursor: 'pointer',
                          mb: '4px'
                        }}
                        key={`${row.original.MitoMap.disease_ch}`}
                      >
                        {row.original.MitoMap.disease_ch}
                      </TypographyA>

                      <TypographyA
                        sx={{
                          width: '100%',
                          maxWidth: 200,
                          lineHeight: '24px',
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                          textOverflow: 'ellipsis',
                          cursor: 'pointer',
                          mb: '4px'
                        }}
                        key={`${row.original.MitoMap.info}`}
                      >
                        {row.original.MitoMap.info}
                      </TypographyA>
                    </Box>
                  </Tooltip>
                ) : (
                  <TypographyA>.</TypographyA>
                )}
              </Stack>
            )
          } else if (
            row.original.OMIM?.geneDiseaseTranslated &&
            row.original.OMIM?.geneDiseaseTranslated.data
          ) {
            let OMIMValue = row.original.OMIM?.geneDiseaseTranslated?.data

            OMIMValue = unique(OMIMValue, 'disease')

            let bgcolor = color.find(
              (i) => i.key === row.original.CLINSIG_final?.toLowerCase()
            )
              ? (color.find(
                  (i) => i.key === row.original.CLINSIG_final?.toLowerCase()
                )?.color as any)
              : 'rgba(0, 0, 0)'

            // loss_of_function等于1 并且CLINSIG_final不等于P/LP
            if (row.original.CLINSIG_final?.toLowerCase() === 'pathogenic') {
              //
            } else if (
              row.original.CLINSIG_final?.toLowerCase() === 'likely pathogenic'
            ) {
              //
            } else if (row.original.loss_of_function === 1) bgcolor = '#f5d18c'

            return (
              <Stack maxWidth={'100%'} spacing={1}>
                {OMIMValue && OMIMValue.length ? (
                  <HtmlTooltip
                    title={
                      <React.Fragment>
                        {OMIMValue.map((item: any, index: any) => (
                          <TypographyA
                            sx={{
                              fontSize: '0.85rem',
                              lineHeight: '1rem',
                              maxWidth: isWindow()
                                ? '400px !important'
                                : '200px !important'
                            }}
                            key={`${item.disease}_${index}`}
                          >
                            {`(${
                              item.genetic_mode ? item.genetic_mode : '.'
                            })-${
                              i18n && i18n.language === 'zh' ? item.disease : ''
                            }`}
                          </TypographyA>
                        ))}
                      </React.Fragment>
                    }
                    placement={'top-start'}
                    TransitionComponent={Slide}
                    sx={{
                      '& .MuiTooltip-tooltip': {
                        maxWidth: isWindow() ? '400px' : '200px'
                      }
                    }}

                    // enterDelay={1000}
                    // onMouseEnter={async (e) => {
                    //   const sourceLanguage = 'en',
                    //     targetLanguage = 'zh'
                    //   const params: TranslateDto = {
                    //     sourceLanguage,
                    //     targetLanguage,
                    //     sourceText: OMIMValue
                    //   }
                    //   Translate.mutateAsync(params)
                    // }}
                  >
                    <Box>
                      {OMIMValue.slice(0, 2).map((items: any, index: any) => {
                        return (
                          <TypographyA
                            sx={{
                              width: '100%',
                              maxWidth: 200,
                              lineHeight: '24px',
                              overflow: 'hidden',
                              whiteSpace: 'nowrap',
                              textOverflow: 'ellipsis',
                              cursor: 'pointer',
                              color:
                                bgcolor === 'rgb(109,56,149,0.8)' ||
                                bgcolor === 'rgb(107,148,200,0.8)' ||
                                bgcolor === 'rgba(229,38,35,0.8)'
                                  ? 'white'
                                  : '#23527c',
                              mb: '4px'
                            }}
                            onClick={() => {
                              props.setCurrent(row.original)

                              props.setOMIMOpen(true)
                            }}
                            key={`${items.disease}_${index}`}
                          >
                            <Chip
                              label={`${
                                items.genetic_mode ? items.genetic_mode : '.'
                              }`}
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: '0.75rem',
                                fontWeight: '700'
                              }}
                            />

                            <span
                              style={{
                                lineHeight: '24px'
                              }}
                            >{`${
                              i18n && i18n.language === 'zh'
                                ? items.disease + '-'
                                : ''
                            }${items.disease}`}</span>
                          </TypographyA>
                        )
                      })}

                      {/* {OMIMValue.length > 3 ? '...' : ''} */}
                    </Box>
                  </HtmlTooltip>
                ) : (
                  <TypographyA>.</TypographyA>
                )}
              </Stack>
            )
          } else {
            let OMIMValue =
              row.original.OMIM?.omimData.map((item) => {
                const inheritances: string[] = []

                if (
                  item &&
                  item.clinicalSynopsis &&
                  item.clinicalSynopsis.INHERITANCE &&
                  Array.isArray(item.clinicalSynopsis.INHERITANCE)
                ) {
                  item.clinicalSynopsis.INHERITANCE.forEach((str: string) =>
                    inheritances.push(str.toLowerCase())
                  )
                }
                const inheritance = formatVariantInheritance(
                  Array.from([...new Set(inheritances)]).join(',')
                )

                return {
                  ...item,
                  inheritance
                }
              }) ?? []

            OMIMValue = unique(OMIMValue, 'phenoName')

            let bgcolor = color.find(
              (i) => i.key === row.original.CLINSIG_final?.toLowerCase()
            )
              ? (color.find(
                  (i) => i.key === row.original.CLINSIG_final?.toLowerCase()
                )?.color as any)
              : 'rgba(0, 0, 0)'

            // loss_of_function等于1 并且CLINSIG_final不等于P/LP
            if (row.original.CLINSIG_final?.toLowerCase() === 'pathogenic') {
              //
            } else if (
              row.original.CLINSIG_final?.toLowerCase() === 'likely pathogenic'
            ) {
              //
            } else if (row.original.loss_of_function === 1) bgcolor = '#f5d18c'

            return (
              <Stack maxWidth={'100%'} spacing={1}>
                {OMIMValue && OMIMValue.length ? (
                  <HtmlTooltip
                    title={
                      <React.Fragment>
                        {OMIMValue.map((item: any, index) => (
                          <TypographyA
                            sx={{
                              fontSize: '0.85rem',
                              lineHeight: '1rem',
                              maxWidth: isWindow()
                                ? '400px !important'
                                : '200px !important'
                            }}
                            key={`${item.phenoName}_${index}`}
                          >
                            {`(${item.inheritance ? item.inheritance : '.'})-${
                              i18n && i18n.language === 'zh'
                                ? item.pheno_name_cn + '-'
                                : ''
                            }${item.phenoName}`}
                          </TypographyA>
                        ))}
                      </React.Fragment>
                    }
                    placement={'top-start'}
                    TransitionComponent={Slide}
                    sx={{
                      '& .MuiTooltip-tooltip': {
                        maxWidth: isWindow() ? '400px' : '200px'
                      }
                    }}

                    // enterDelay={1000}
                    // onMouseEnter={async (e) => {
                    //   const sourceLanguage = 'en',
                    //     targetLanguage = 'zh'
                    //   const params: TranslateDto = {
                    //     sourceLanguage,
                    //     targetLanguage,
                    //     sourceText: OMIMValue
                    //   }
                    //   Translate.mutateAsync(params)
                    // }}
                  >
                    <Box>
                      {OMIMValue.slice(0, 2).map((items: any, index) => {
                        return (
                          <TypographyA
                            sx={{
                              width: '100%',
                              maxWidth: 200,
                              lineHeight: '24px',
                              overflow: 'hidden',
                              whiteSpace: 'nowrap',
                              textOverflow: 'ellipsis',
                              cursor: 'pointer',
                              color:
                                bgcolor === 'rgb(109,56,149,0.8)' ||
                                bgcolor === 'rgb(107,148,200,0.8)' ||
                                bgcolor === 'rgba(229,38,35,0.8)'
                                  ? 'white'
                                  : '#23527c',
                              mb: '4px'
                            }}
                            onClick={() => {
                              props.setCurrent(row.original)

                              props.setOMIMOpen(true)
                            }}
                            key={`${items.phenoMim}_${index}`}
                          >
                            <Chip
                              label={`${
                                items.inheritance ? items.inheritance : '.'
                              }`}
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: '0.75rem',
                                fontWeight: '700'
                              }}
                            />

                            <span
                              style={{
                                lineHeight: '24px'
                              }}
                            >{`${
                              i18n && i18n.language === 'zh'
                                ? items.pheno_name_cn + '-'
                                : ''
                            }${items.phenoName}`}</span>
                          </TypographyA>
                        )
                      })}

                      {/* {OMIMValue.length > 3 ? '...' : ''} */}
                    </Box>
                  </HtmlTooltip>
                ) : (
                  <TypographyA>.</TypographyA>
                )}
              </Stack>
            )
          }
        }
      },
      {
        accessorKey: 'SampleInfo',
        id: 'SampleInfo',
        header: () => (
          <Stack maxWidth={'100%'} direction={'row'} alignItems="center">
            <Stack maxWidth={'100%'}>
              <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                {t('Sample Info')}
              </span>
            </Stack>
          </Stack>
        ),
        enableSorting: false,
        footer: (props) => props.column.id,
        cell: ({ row, getValue }) => {
          return (
            <Stack maxWidth={'100%'} direction={'column'} spacing={1}>
              <Button
                variant="contained"
                onClick={() => {
                  props.setSampleInfoOpen(true)

                  props.setCurrent(row.original)
                }}
                size={'small'}
              >
                {t('F score')}
              </Button>
            </Stack>
          )
        }
      }
    ]

    if (!props.indexChecked) {
      column = column.filter((i) => i.id !== 'index')
    }
    return column
  }, [props, user])

  React.useImperativeHandle(ref, () => ({
    column
  }))

  return <></>
})

export default columns1CohortSites
